import React, { useCallback, useMemo, useState } from 'react'
import { SearchOutlined } from '@ant-design/icons'
import { Alert, Dropdown, Flex, Input, Spin, theme, Button, Typography } from 'antd'
import _isEmpty from 'lodash/isEmpty'
import { PAGE_PARAM, SEARCH_PARAM, ASSIGNMENT_SEARCH_PARAM, SEARCH_TYPE_PARAM } from '../../../constants/query-params'
import useQueryParam from '../../../hooks/useQueryParam'
import useQueryParamSetter from '../../../hooks/useQueryParamSetter'
import useDebounce from '../../../hooks/useDebounce'
import { useSearchAssignmentsQuery } from '../../../store/apis/assignment.api'
import { buildAssignmentPath } from '../../../utils/link.helper'
import { Link, useNavigate } from 'react-router-dom'
import { isNewTextAssignment } from '../../../utils/assignment.helper'
import Routes from '../../../constants/routes'
import { AssignmentSearchTypes } from '../../../constants/assignment-search-types'
import useRecentAssignments from '../../../hooks/useRecentAssignments'

const { Text } = Typography

// Maximum number of items to show before "Show all" button appears
const MAX_VISIBLE_ITEMS = 5
const SEARCH_DEBOUNCE_TIME = 500
const BLUR_DELAY = 250

/**
 * GlobalSearch component displays a search dropdown with results from assignments
 * 
 * TODO: Consider supporting also organization assignments search
 */
const GlobalSearch = () => {
    const searchParam = useQueryParam(SEARCH_PARAM)
    const setQueryParam = useQueryParamSetter()
    const navigate = useNavigate()
    
    const [searchInputFocused, setSearchInputFocused] = useState(false)

    // Recent assignments hook for empty search state
    const { recentAssignments, isFetching: isFetchingRecentAssignments } = useRecentAssignments()

    // API query with skip if no search term
    const {
        data: searchResults,
        isFetching: isFetchingSearchResults,
        error: errorFetchingSearchResults,
    } = useSearchAssignmentsQuery(
        { name: searchParam }, 
        { skip: !searchParam }
    )

    // Navigate to assignments page with current search param
    const navigateToSearchPage = useCallback((route, searchType = AssignmentSearchTypes.MINE) => {
        navigate({
            pathname: route,
            search: `?${ASSIGNMENT_SEARCH_PARAM}=${encodeURIComponent(searchParam || '')}&${SEARCH_TYPE_PARAM}=${searchType}`
        })
        setSearchInputFocused(false)
    }, [navigate, searchParam])

    // Create assignment link
    const createAssignmentLink = useCallback((assignment) => (
        <Link
            to={buildAssignmentPath(
                assignment.token,
                isNewTextAssignment(assignment.language) ? 'all' : assignment.defaultLayerId,
            )}
        >
            {assignment.name}
        </Link>
    ), [])

    // Create "Show all" button for a specific route
    const createShowAllButton = useCallback((route, key, searchType = AssignmentSearchTypes.MINE) => ({
        key,
        label: (
            <Button
                type="link"
                onClick={() => navigateToSearchPage(route, searchType)}
                style={{ width: '100%', textAlign: 'left' }}
            >
                Show All
            </Button>
        ),
    }), [navigateToSearchPage])

    // Build dropdown menu items
    const items = useMemo(() => {
        const menuItems = []
        
        // Regular assignments group
        if (searchResults?.assignments?.length) {
            const assignmentsGroup = {
                key: 'assignments',
                type: 'group',
                label: 'Assignments',
                children: searchResults.assignments.map(assignment => ({
                    key: assignment.id,
                    label: createAssignmentLink(assignment),
                })),
            }
            
            // Add "Show all" button if at limit
            if (searchResults.assignments.length >= MAX_VISIBLE_ITEMS) {
                assignmentsGroup.children.push(
                    createShowAllButton(Routes.ASSIGNMENTS, 'show-more-assignments')
                )
            }
            
            menuItems.push(assignmentsGroup)
        }
        
        // Shared assignments group
        if (searchResults?.sharedAssignments?.length) {
            const sharedAssignmentsGroup = {
                key: 'shared-assignments',
                type: 'group',
                label: 'Shared Assignments',
                children: searchResults.sharedAssignments.map(assignment => ({
                    key: `shared-${assignment.id}`,
                    label: createAssignmentLink(assignment),
                })),
            }
            
            // Add "Show all" button if at limit
            if (searchResults.sharedAssignments.length >= MAX_VISIBLE_ITEMS) {
                sharedAssignmentsGroup.children.push(
                    createShowAllButton(Routes.ASSIGNMENTS, 'show-more-shared-assignments', AssignmentSearchTypes.SHARED)
                )
            }
            
            menuItems.push(sharedAssignmentsGroup)
        }
        
        return menuItems
    }, [searchResults, createAssignmentLink, createShowAllButton])

    // Build recent assignments menu items (when no search term)
    const recentItems = useMemo(() => {
        if (!recentAssignments.length) return []
        return [{
            key: 'recent-assignments',
            type: 'group',
            label: 'Recent assignments',
            children: recentAssignments.map(assignment => ({
                key: `recent-${assignment.token}`,
                label: (
                    <Link
                        to={buildAssignmentPath(
                            assignment.token,
                            isNewTextAssignment(assignment.language) ? 'all' : (assignment.layerId ?? 0),
                        )}
                    >
                        {assignment.name}
                    </Link>
                ),
            })),
        }]
    }, [recentAssignments])

    // Handle search with debounce
    const debouncedSetQueryParam = useDebounce(
        (searchValue) => {
            setQueryParam({ 
                [SEARCH_PARAM]: searchValue || null,
                [PAGE_PARAM]: null 
            })
        },
        SEARCH_DEBOUNCE_TIME,
        [setQueryParam]
    )

    const handleSearchChange = useCallback(event => {
        const searchValue = event.target.value
        debouncedSetQueryParam(searchValue)
    }, [debouncedSetQueryParam])

    // Theme styles for dropdown
    const { token } = theme.useToken()
    const dropdownContentStyle = {
        backgroundColor: token.colorBgElevated,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
        padding: 8,
    }

    const displayItems = searchParam ? items : recentItems
    const isLoading = searchParam ? isFetchingSearchResults : isFetchingRecentAssignments

    // Custom dropdown render function
    const dropdownRender = useCallback((menu) => {
        if (isLoading) {
            return (
                <Flex justify='center' style={dropdownContentStyle}>
                    <Spin spinning tip={searchParam ? 'Searching...' : 'Loading...'} />
                </Flex>
            )
        }
        
        if (searchParam && errorFetchingSearchResults) {
            return (
                <Flex justify='center' style={dropdownContentStyle}>
                    <Alert 
                        message="Search error" 
                        description={errorFetchingSearchResults.message || "Failed to fetch search results"} 
                        type='error' 
                        showIcon 
                    />
                </Flex>
            )
        }
        
        if (_isEmpty(displayItems)) {
            return (
                <Flex justify='center' style={dropdownContentStyle}>
                    <Text type='secondary'>
                        {searchParam ? 'Your search matched nothing' : 'No recent assignments'}
                    </Text>
                </Flex>
            )
        }
        
        return menu
    }, [isLoading, searchParam, errorFetchingSearchResults, displayItems, dropdownContentStyle])

    // Handle blur with timeout to allow clicking dropdown items
    const handleBlur = useCallback(() => {
        setTimeout(() => setSearchInputFocused(false), BLUR_DELAY)
    }, [])

    // Handle focus
    const handleFocus = useCallback(() => {
        setSearchInputFocused(true)
    }, [])

    return (
        <Dropdown 
            open={searchInputFocused} 
            menu={{ items: displayItems }} 
            dropdownRender={dropdownRender}
        >
            <Input
                id='search'
                size='large'
                aria-label='search'
                autoComplete='off'
                placeholder='Search or jump to...'
                allowClear
                prefix={<SearchOutlined />}
                style={{ width: 240 }}
                onChange={handleSearchChange}
                defaultValue={searchParam}
                data-test="global-search-input"
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
        </Dropdown>
    )
}

export default React.memo(GlobalSearch)
